import { useState, useCallback, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import AdoptionLocationPicker from '@/components/Adoption/AdoptionLocationPicker';
import { Upload, Save, Eye, Search } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useTranslation } from '@/contexts/TranslationContext';

interface CreateAdoptionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  existingPost?: any;
}

export default function CreateAdoptionModal({ 
  open, 
  onOpenChange, 
  onSuccess,
  existingPost 
}: CreateAdoptionModalProps) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageUrl, setImageUrl] = useState(existingPost?.image_url || '');
  const [locationData, setLocationData] = useState<{
    latitude: number;
    longitude: number;
    address: string;
  } | null>(existingPost ? {
    latitude: existingPost.latitude || 0,
    longitude: existingPost.longitude || 0,
    address: existingPost.location || ''
  } : null);

  const [formData, setFormData] = useState({
    title: existingPost?.title || '',
    pet_name: existingPost?.pet_name || '',
    species: existingPost?.species || '',
    breed: existingPost?.breed || '',
    age: existingPost?.age || '',
    size: existingPost?.size || '',
    gender: existingPost?.gender || '',
    character_traits: existingPost?.character_traits || '',
    health_info: existingPost?.health_info || '',
    description: existingPost?.description || '',
    location: existingPost?.location || '',
    adoption_conditions: existingPost?.adoption_conditions || ''
  });

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Geocoding function to search for location
  const searchLocation = useCallback(async (query: string) => {
    if (!query.trim()) return;
    
    setSearchLoading(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1&countrycodes=it`
      );
      const data = await response.json();
      
      if (data.length > 0) {
        const result = data[0];
        const lat = parseFloat(result.lat);
        const lon = parseFloat(result.lon);
        
        setLocationData({
          latitude: lat,
          longitude: lon,
          address: result.display_name
        });
        
        toast({
          title: "Posizione trovata!",
          description: "La posizione è stata aggiornata sulla mappa",
        });
      } else {
        toast({
          title: "Posizione non trovata",
          description: "Prova con un termine di ricerca diverso",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Errore di ricerca",
        description: "Impossibile cercare la posizione",
        variant: "destructive",
      });
    } finally {
      setSearchLoading(false);
    }
  }, [toast]);

  // Handle location field change with search
  const handleLocationChange = (value: string) => {
    handleInputChange('location', value);
    // Don't clear location data when user is typing
  };

  const handleLocationSearch = () => {
    if (formData.location.trim()) {
      searchLocation(formData.location);
    }
  };

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setImageFile(file);
      setImageUrl(URL.createObjectURL(file));
    }
  };

  const uploadImage = async (): Promise<string | null> => {
    if (!imageFile) return imageUrl || null;

    try {
      const fileExt = imageFile.name.split('.').pop();
      const fileName = `adoption-${Date.now()}.${fileExt}`;

      const { data, error } = await supabase.storage
        .from('post-images')
        .upload(fileName, imageFile);

      if (error) throw error;

      const { data: { publicUrl } } = supabase.storage
        .from('post-images')
        .getPublicUrl(fileName);

      return publicUrl;
    } catch (error) {
      console.error('Error uploading image:', error);
      toast({
        title: "Errore upload",
        description: "Impossibile caricare l'immagine",
        variant: "destructive",
      });
      return null;
    }
  };

  const handleSubmit = async (isDraft: boolean) => {
    if (!user) return;

    if (!formData.title || !formData.pet_name || !formData.species) {
      toast({
        title: "Campi obbligatori",
        description: "Compila almeno titolo, nome pet e specie",
        variant: "destructive",
      });
      return;
    }

    if (!isDraft && !locationData) {
      toast({
        title: "Posizione richiesta",
        description: "Seleziona una posizione sulla mappa per pubblicare l'annuncio",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      const uploadedImageUrl = await uploadImage();

      const postData = {
        ...formData,
        user_id: user.id,
        age: formData.age ? parseInt(formData.age) : null,
        image_url: uploadedImageUrl,
        status: isDraft ? 'draft' : 'published',
        latitude: locationData?.latitude || null,
        longitude: locationData?.longitude || null,
        location: locationData?.address || formData.location
      };

      let error;
      if (existingPost) {
        const { error: updateError } = await supabase
          .from('adoption_posts')
          .update(postData)
          .eq('id', existingPost.id);
        error = updateError;
      } else {
        const { error: insertError } = await supabase
          .from('adoption_posts')
          .insert([postData]);
        error = insertError;
      }

      if (error) throw error;

      toast({
        title: existingPost ? "Annuncio aggiornato" : "Annuncio creato",
        description: `L'annuncio è stato ${isDraft ? 'salvato come bozza' : 'pubblicato'} con successo`,
      });

      onSuccess();
      onOpenChange(false);
    } catch (error) {
      console.error('Error saving adoption post:', error);
      toast({
        title: "Errore",
        description: "Impossibile salvare l'annuncio",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {existingPost ? 'Modifica Annuncio di Adozione' : 'Crea Annuncio di Adozione'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Immagine */}
          <div className="space-y-2">
            <Label>Foto del Pet *</Label>
            <div className="flex items-center space-x-4">
              <Avatar className="w-20 h-20">
                <AvatarImage src={imageUrl} alt="Pet" />
                <AvatarFallback>{formData.pet_name[0] || 'P'}</AvatarFallback>
              </Avatar>
              <div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  className="hidden"
                  id="image-upload"
                />
                <Label htmlFor="image-upload" className="cursor-pointer">
                  <div className="flex items-center space-x-2 px-4 py-2 bg-primary rounded-md text-white hover:bg-primary/90">
                    <Upload className="w-4 h-4" />
                    <span>Carica Foto</span>
                  </div>
                </Label>
              </div>
            </div>
          </div>

          {/* Info di base */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="title">Titolo Annuncio *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="es. Cerca casa per Luna"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pet_name">Nome Pet *</Label>
              <Input
                id="pet_name"
                value={formData.pet_name}
                onChange={(e) => handleInputChange('pet_name', e.target.value)}
                placeholder="Nome del pet"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="species">Specie *</Label>
              <Select value={formData.species} onValueChange={(value) => handleInputChange('species', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona specie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Cane">Cane</SelectItem>
                  <SelectItem value="Gatto">Gatto</SelectItem>
                  <SelectItem value="Coniglio">Coniglio</SelectItem>
                  <SelectItem value="Altro">Altro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="breed">Razza</Label>
              <Input
                id="breed"
                value={formData.breed}
                onChange={(e) => handleInputChange('breed', e.target.value)}
                placeholder="Razza o meticcio"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="age">Età (anni)</Label>
              <Input
                id="age"
                type="number"
                value={formData.age}
                onChange={(e) => handleInputChange('age', e.target.value)}
                placeholder="0"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="size">Taglia</Label>
              <Select value={formData.size} onValueChange={(value) => handleInputChange('size', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Taglia" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Piccola">Piccola</SelectItem>
                  <SelectItem value="Media">Media</SelectItem>
                  <SelectItem value="Grande">Grande</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="gender">Sesso</Label>
              <Select value={formData.gender} onValueChange={(value) => handleInputChange('gender', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Sesso" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Maschio">Maschio</SelectItem>
                  <SelectItem value="Femmina">Femmina</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="character_traits">Carattere</Label>
            <Input
              id="character_traits"
              value={formData.character_traits}
              onChange={(e) => handleInputChange('character_traits', e.target.value)}
              placeholder="es. Giocherellone, dolce, energico"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="health_info">Salute</Label>
            <Input
              id="health_info"
              value={formData.health_info}
              onChange={(e) => handleInputChange('health_info', e.target.value)}
              placeholder="Vaccini, sterilizzazione, microchip..."
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Descrizione</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Racconta di più sul pet..."
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="location">Zona/Comune</Label>
            <div className="flex gap-2">
              <Input
                id="location"
                value={formData.location}
                onChange={(e) => handleLocationChange(e.target.value)}
                placeholder="es. Milano, Roma..."
                className="flex-1"
              />
              <Button
                type="button"
                onClick={handleLocationSearch}
                disabled={searchLoading || !formData.location.trim()}
                size="sm"
                variant="outline"
              >
                <Search className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Scrivi una località e clicca il pulsante di ricerca per trovarla sulla mappa
            </p>
          </div>

          {/* Location Picker */}
          <AdoptionLocationPicker
            onLocationSelect={(latitude, longitude, address) => {
              setLocationData({ latitude, longitude, address });
              // Only update location field if it's empty or user hasn't typed anything meaningful
              if (!formData.location.trim() || formData.location === locationData?.address) {
                setFormData(prev => ({ ...prev, location: address }));
              }
            }}
            initialLocation={locationData}
          />

          <div className="space-y-2">
            <Label htmlFor="adoption_conditions">Condizioni di Adozione</Label>
            <Textarea
              id="adoption_conditions"
              value={formData.adoption_conditions}
              onChange={(e) => handleInputChange('adoption_conditions', e.target.value)}
              placeholder="Pre-affido, disponibilità incontro, requisiti..."
              rows={2}
            />
          </div>

          <div className="flex space-x-3">
            <Button
              onClick={() => handleSubmit(true)}
              disabled={loading}
              variant="outline"
              className="flex-1"
            >
              <Save className="w-4 h-4 mr-2" />
              Salva Bozza
            </Button>
            <Button
              onClick={() => handleSubmit(false)}
              disabled={loading}
              className="flex-1"
            >
              <Eye className="w-4 h-4 mr-2" />
              Pubblica
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}